import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import apiManager from '@/api/AxiosInstance';
import newApiManager from '@/api/NewAxiosInstance';
import { ADDRESS2, ADDRESS3 } from '@/const/products';
import { productsFormAtom } from '@/store/products';
import { getFormatDateOnlyNum } from '@/utils/common';

const keepEnumDescIfMissing = (
  prev: ProductDetailResponse,
  next: ProductDetailResponse,
  key: 'loaded' | 'loadedDetail',
): ProductDetailResponse => {
  const prevVal = prev?.[key] as EnumPresenter | undefined;
  const nextVal = next?.[key] as EnumPresenter | undefined;

  if (!nextVal) return next;
  const isDescMissing = nextVal.desc == null || String(nextVal.desc).trim() === '';
  const isSameCode = prevVal?.code && nextVal.code && String(prevVal.code) === String(nextVal.code);

  if (isDescMissing && isSameCode && prevVal?.desc) {
    return {
      ...next,
      [key]: { ...nextVal, desc: prevVal.desc },
    } as ProductDetailResponse;
  }

  return next;
};

const isOneTonRangeTons = (tons: unknown) => {
  const n = Number(tons);
  return n === 1 || n === 1.1 || n === 1.2;
};

const keepLoadedIfMissingWithinOneTonRange = (prev: ProductDetailResponse, next: ProductDetailResponse) => {
  const prevInRange = isOneTonRangeTons(prev?.tons);
  const nextInRange = isOneTonRangeTons(next?.tons);
  if (!prevInRange || !nextInRange) return next;

  const nextLoadedCode = next?.loaded?.code;
  const isNextLoadedMissing = next?.loaded == null || nextLoadedCode == null || String(nextLoadedCode).trim() === '';

  if (isNextLoadedMissing && prev?.loaded?.code) {
    return {
      ...next,
      loaded: prev.loaded,
      loadedDetail: next.loadedDetail ?? prev.loadedDetail,
    } as ProductDetailResponse;
  }

  return next;
};

const useFetchProductData = (id: string | undefined, shouldFetch: boolean) => {
  const setProductFormData = useSetAtom(productsFormAtom);
  const [productSearchParams, setProductSearchParams] = useState<ProductSearchParams>();

  useEffect(() => {
    const fetchData = async () => {
      if (shouldFetch && id) {
        try {
          const response = await newApiManager.get(`/api/v1/products/${id}`);
          const responseData: ProductDetailResponse = response.data;
          setProductFormData((prev) => {
            const merged0 = keepLoadedIfMissingWithinOneTonRange(prev, responseData);
            const merged1 = keepEnumDescIfMissing(prev, merged0, 'loaded');
            const merged2 = keepEnumDescIfMissing(prev, merged1, 'loadedDetail');
            return merged2;
          });
          const { tons, model, loaded, loadedInnerLength, manufacturerCategories, axis, firstRegistrationDate } =
            responseData;
          const searchParams = {
            tons,
            model,
            loaded,
            loadedInnerLength,
            manufacturerCategories,
            axis,
            year: getFormatDateOnlyNum(firstRegistrationDate, ['year']),
          };
          setProductSearchParams(searchParams as ProductSearchParams);
        } catch (error) {
          console.log(error);
        }
      }
    };
    fetchData();
  }, [id, shouldFetch, setProductFormData]);

  return productSearchParams;
};

export default useFetchProductData;
